import { connect } from 'react-redux';
import { isEqual, omit, snakeCase } from 'lodash';
import { useForm, useWatch } from 'react-hook-form';
import i18next from 'i18next';
import React, { useState, useEffect } from 'react';

import { loading, loadingSuccess } from '@actions/loading';
import { showErrorMessage, showSuccessMessage } from '@actions/messageconfirmation';

import { Button, HoverTooltip, ToggleButton } from '@commons/utils/styledLibraryComponents';
import { getTheme } from '@commons/utils/theme';
import { LeftRightSplitter } from '@commons/LeftRightSplitter';
import GenericForm, { buildSchema } from '@commons/GenericForm';
import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';
import WhiteCard from '@commons/WhiteCard';

import { yupResolver } from '@hookform/resolvers/yup';

import { Divider, TextField } from '@mui/material';

import InputTagChip from '@lib/inpulse/InputTagChip';

import invoiceService from '@services/invoice';

import {
  Container,
  ContentContainer,
  HeaderContainer,
  HeaderLeftContainer,
  CustomPromptContainer,
  ContentHeaderButtonContainer,
  LeftCardContent,
} from './styledComponents';
import { DisabledTooltip } from '@commons/DisabledTooltip';
import utilsForm from './utils/form';

const InvoiceControlCustomPrompt = (props) => {
  const {
    supplierId,
    // redux states
    showErrorMessage,
    showSuccessMessage,
    pageLoading,
    pageLoaded,
  } = props;

  const theme = getTheme();

  const MODES = [
    {
      key: 'form',
      name: 'Formulaire',
    },
    {
      key: 'prompt',
      name: 'Prompt',
    },
  ];

  const [selectedMode, setSelectedMode] = useState(MODES[0]);
  const [fullPrompt, setFullPrompt] = useState('');
  const [isFullPromptUpdated, setIsFullPromptUpdated] = useState(false);
  const [fetchedCustomPrompt, setFetchedCustomPrompt] = useState(null);
  const [defaultPrompt, setDefaultPrompt] = useState('');
  const [customFieldsPrompt, setCustomFieldsPrompt] = useState('');
  const [supplierAliases, setSupplierAliases] = useState('');

  const inputs = utilsForm.getInputs({
    isDisabled: isEqual(selectedMode, MODES[1]),
  });

  const form = useForm({
    defaultValues: {},
    resolver: yupResolver(buildSchema(inputs)),
  });

  const formFields = useWatch({
    control: form.control,
  });

  useEffect(() => {
    (async () => getSupplierCustomPrompt())();
  }, []);

  useEffect(() => {
    if (!fetchedCustomPrompt) {
      return;
    }

    form.reset({
      invoiceNumber: fetchedCustomPrompt.invoice_number,
      excludingTaxesInvoiceTotal: fetchedCustomPrompt.excluding_taxes_invoice_total,
      invoiceDate: fetchedCustomPrompt.invoice_date,
      deliveryDate: fetchedCustomPrompt.delivery_date,
      orderReference: fetchedCustomPrompt.order_reference,
      deliveryReference: fetchedCustomPrompt.delivery_reference,
      code: fetchedCustomPrompt.code,
      name: fetchedCustomPrompt.name,
      quantity: fetchedCustomPrompt.quantity,
      unitPrice: fetchedCustomPrompt.unit_price,
      packaging: fetchedCustomPrompt.packaging,
    });

    setSupplierAliases(fetchedCustomPrompt.supplier_aliases || '');

    if (fetchedCustomPrompt.full_prompt) {
      setFullPrompt(fetchedCustomPrompt.full_prompt);
      setSelectedMode(
        isEqual(fetchedCustomPrompt.full_prompt, customFieldsPrompt) ? MODES[0] : MODES[1],
      );
    }
  }, [fetchedCustomPrompt]);

  useEffect(() => {
    if (!fullPrompt || !fetchedCustomPrompt || !fetchedCustomPrompt.full_prompt) {
      return;
    }

    setIsFullPromptUpdated(!isEqual(fullPrompt, fetchedCustomPrompt.full_prompt));
  }, [fullPrompt, fetchedCustomPrompt]);

  const getSupplierCustomPrompt = async () => {
    pageLoading();

    try {
      const [customPrompt, defaultPrompt, customFieldsPrompt] =
        await invoiceService.getSupplierCustomPrompt(supplierId);

      if (!customPrompt) {
        return;
      }

      setCustomFieldsPrompt(customFieldsPrompt);
      setDefaultPrompt(defaultPrompt);

      setFetchedCustomPrompt(customPrompt);
    } catch {
      showErrorMessage(i18next.t('ADMIN.SUPPLIERS.CUSTOM_PROMPT_FETCH_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const handleSelectedModeChange = (mode) => {
    if (isEqual(mode, selectedMode)) {
      return;
    }

    if (isFullPromptUpdated) {
      setSelectedMode(MODES[1]);
      return;
    }

    setSelectedMode(mode);
  };

  const resetPromptToFetchData = () => {
    setFullPrompt(fetchedCustomPrompt.full_prompt);
  };

  const resetPromptToDefaultPrompt = async () => {
    pageLoading();
    try {
      const [customPrompt, defaultPrompt] = await invoiceService.getSupplierCustomPrompt(
        supplierId,
        true,
      );

      if (!customPrompt || !customPrompt.full_prompt) {
        return;
      }

      setDefaultPrompt(defaultPrompt);

      setFullPrompt(customPrompt.full_prompt);
    } catch {
      showErrorMessage(i18next.t('ADMIN.SUPPLIERS.CUSTOM_PROMPT_FETCH_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const customFieldsChanged = () => {
    const fetchedFields = omit(fetchedCustomPrompt, [
      'id',
      'supplier_id',
      'full_prompt',
      'supplier_aliases',
    ]);

    const formattedFormFields = Object.entries(formFields).reduce((acc, [key, value]) => {
      if (!(value || '').trim()) {
        acc[snakeCase(key)] = null;
        return acc;
      }

      acc[snakeCase(key)] = value;

      return acc;
    }, {});

    return !isEqual(fetchedFields, formattedFormFields);
  };

  const customFieldsDefault = () =>
    Object.values(formFields).every((value) => !(value || '').trim());

  const aliasesChanged = () => {
    const fetchedAliases = fetchedCustomPrompt?.supplier_aliases || null;
    const currentAliases = supplierAliases.trim() || null;

    return !isEqual(fetchedAliases, currentAliases);
  };

  const resetCustomFieldsToFetchData = () => {
    form.reset({
      invoiceNumber: fetchedCustomPrompt.invoice_number || '',
      excludingTaxesInvoiceTotal: fetchedCustomPrompt.excluding_taxes_invoice_total || '',
      invoiceDate: fetchedCustomPrompt.invoice_date || '',
      deliveryDate: fetchedCustomPrompt.delivery_date || '',
      orderReference: fetchedCustomPrompt.order_reference || '',
      deliveryReference: fetchedCustomPrompt.delivery_reference || '',
      code: fetchedCustomPrompt.code || '',
      name: fetchedCustomPrompt.name || '',
      quantity: fetchedCustomPrompt.quantity || '',
      unitPrice: fetchedCustomPrompt.unit_price || '',
      packaging: fetchedCustomPrompt.packaging || '',
    });
  };

  const resetCustomFieldsToEmpty = () => {
    form.reset({
      invoiceNumber: '',
      excludingTaxesInvoiceTotal: '',
      invoiceDate: '',
      deliveryDate: '',
      orderReference: '',
      deliveryReference: '',
      code: '',
      name: '',
      quantity: '',
      unitPrice: '',
      packaging: '',
    });
  };

  const handleSaveLeft = async () => {
    pageLoading();

    const formattedFields = Object.entries(formFields).reduce((acc, [key, value]) => {
      if (!(value || '').trim()) {
        acc[key] = null;
        return acc;
      }

      acc[key] = value;

      return acc;
    }, {});

    try {
      await invoiceService.upsertSupplierCustomPrompt(supplierId, {
        ...formattedFields,
        supplierAliases: supplierAliases.trim() || null,
        fullPrompt: fetchedCustomPrompt?.full_prompt || null,
      });

      await getSupplierCustomPrompt();

      showSuccessMessage(i18next.t('ADMIN.SUPPLIERS.CUSTOM_PROMPT_SAVE_SUCCESS'));
    } catch {
      showErrorMessage(i18next.t('ADMIN.SUPPLIERS.CUSTOM_PROMPT_SAVE_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const handleSavePrompt = async () => {
    pageLoading();

    try {
      await invoiceService.upsertSupplierCustomPrompt(supplierId, {
        invoiceNumber: fetchedCustomPrompt?.invoice_number || null,
        excludingTaxesInvoiceTotal: fetchedCustomPrompt?.excluding_taxes_invoice_total || null,
        invoiceDate: fetchedCustomPrompt?.invoice_date || null,
        deliveryDate: fetchedCustomPrompt?.delivery_date || null,
        orderReference: fetchedCustomPrompt?.order_reference || null,
        deliveryReference: fetchedCustomPrompt?.delivery_reference || null,
        code: fetchedCustomPrompt?.code || null,
        name: fetchedCustomPrompt?.name || null,
        quantity: fetchedCustomPrompt?.quantity || null,
        unitPrice: fetchedCustomPrompt?.unit_price || null,
        packaging: fetchedCustomPrompt?.packaging || null,
        supplierAliases: fetchedCustomPrompt?.supplier_aliases || null,
        fullPrompt: fullPrompt.trim() || null,
      });

      await getSupplierCustomPrompt();

      showSuccessMessage(i18next.t('ADMIN.SUPPLIERS.CUSTOM_PROMPT_SAVE_SUCCESS'));
    } catch {
      showErrorMessage(i18next.t('ADMIN.SUPPLIERS.CUSTOM_PROMPT_SAVE_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const isLeftSaveDisabled = !customFieldsChanged() && !aliasesChanged();

  const leftCardBackground = isEqual(selectedMode, MODES[0])
    ? theme.colors.greys.lightest
    : theme.colors.greys.lighter;

  const renderLeftCardContent = () => (
    <LeftCardContent>
      <GenericForm
        customStyle={{
          flexWrap: 'wrap',
          gap: '24px',
          alignItems: 'flex-start',
        }}
        form={form}
        inputs={inputs}
        isEditionAllowed={isEqual(selectedMode, MODES[0])}
      />
      <Divider />
      <InputTagChip
        label={i18next.t('ADMIN.SUPPLIERS.CUSTOM_PROMPT_OTHER_SUPPLIER_NAMES')}
        tooltipText={i18next.t('ADMIN.SUPPLIERS.CUSTOM_PROMPT_OTHER_SUPPLIER_NAMES_TOOLTIP')}
        value={supplierAliases}
        onChange={setSupplierAliases}
      />
    </LeftCardContent>
  );

  return (
    <Container>
      <HeaderContainer>
        <LeftRightSplitter
          left={
            <HeaderLeftContainer>
              <HoverTooltip
                isTooltipDisplayed={isFullPromptUpdated}
                renderContent={() => (
                  <DisabledTooltip
                    text={i18next.t('ADMIN.SUPPLIERS.CUSTOM_PROMPT_TOGGLE_DISABLED_TOOLTIP')}
                  />
                )}
                style={{ width: '240px' }}
              >
                <ToggleButton
                  choices={MODES}
                  isDisabled={isFullPromptUpdated}
                  selectedChoice={selectedMode}
                  setSelectedChoice={handleSelectedModeChange}
                />
              </HoverTooltip>
            </HeaderLeftContainer>
          }
          right={null}
          width={'100%'}
        />
      </HeaderContainer>
      <ContentContainer>
        <WhiteCard
          customStyle={{
            width: '50%',
            overflow: 'auto',
            backgroundColor: leftCardBackground,
          }}
          maxWidth={'100%'}
          renderContent={renderLeftCardContent()}
          title={
            <LeftRightSplitter
              left={
                <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.H2_INTER}>
                  {i18next.t('ADMIN.SUPPLIERS.CUSTOM_PROMPT_LEFT_TITLE')}
                </Text>
              }
              right={
                <ContentHeaderButtonContainer>
                  {fetchedCustomPrompt && (
                    <Button
                      color="inpulse-outline"
                      handleClick={resetCustomFieldsToFetchData}
                      icon={'/images/inpulse/sync-black-small.svg'}
                      isDisabled={isEqual(selectedMode, MODES[1]) || !customFieldsChanged()}
                    />
                  )}
                  <Button
                    color="inpulse-outline"
                    handleClick={resetCustomFieldsToEmpty}
                    icon={'/images/inpulse/delete-black-small.svg'}
                    isDisabled={isEqual(selectedMode, MODES[1]) || customFieldsDefault()}
                  />
                  <Button
                    color="inpulse-default"
                    handleClick={handleSaveLeft}
                    icon={'/images/inpulse/save-white-small.svg'}
                    isDisabled={isLeftSaveDisabled}
                    label={i18next.t('GENERAL.SAVE')}
                  />
                </ContentHeaderButtonContainer>
              }
              width={'100%'}
            />
          }
          width={'50%'}
        />
        <CustomPromptContainer
          style={{
            backgroundColor: isEqual(selectedMode, MODES[0])
              ? theme.colors.greys.lighter
              : theme.colors.greys.lightest,
          }}
        >
          <LeftRightSplitter
            left={
              <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.H2_INTER}>
                {i18next.t('ADMIN.SUPPLIERS.CUSTOM_PROMPT_RIGHT_TITLE')}
              </Text>
            }
            right={
              <ContentHeaderButtonContainer>
                {fetchedCustomPrompt && (
                  <Button
                    color="inpulse-outline"
                    handleClick={resetPromptToFetchData}
                    icon={'/images/inpulse/sync-black-small.svg'}
                    isDisabled={
                      isEqual(selectedMode, MODES[0]) ||
                      isEqual(fetchedCustomPrompt.full_prompt, fullPrompt)
                    }
                  />
                )}
                <Button
                  color="inpulse-outline"
                  handleClick={resetPromptToDefaultPrompt}
                  icon={'/images/inpulse/delete-black-small.svg'}
                  isDisabled={
                    isEqual(selectedMode, MODES[0]) ||
                    (!isFullPromptUpdated &&
                      isEqual(fetchedCustomPrompt?.full_prompt, defaultPrompt))
                  }
                />
                <Button
                  color="inpulse-default"
                  handleClick={handleSavePrompt}
                  icon={'/images/inpulse/save-white-small.svg'}
                  isDisabled={isEqual(selectedMode, MODES[0]) || !isFullPromptUpdated}
                  label={i18next.t('GENERAL.SAVE')}
                />
              </ContentHeaderButtonContainer>
            }
            width={'100%'}
          />

          <TextField
            autoComplete="off"
            disabled={isEqual(selectedMode, MODES[0])}
            sx={{
              width: '100%',
              height: '100%',
            }}
            value={fullPrompt}
            multiline
            onChange={(event) => {
              const value = event.target.value;

              setFullPrompt(value);
            }}
          />
        </CustomPromptContainer>
      </ContentContainer>
    </Container>
  );
};

const mapDispatchToProps = (dispatch) => ({
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  showSuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
});

export default connect(null, mapDispatchToProps)(InvoiceControlCustomPrompt);
